<template>
  <form @submit.prevent="save_config">
    <div class="tc-packages">
      <!-- no intro copy for staff: the table says what it is. The read-only
           audience keeps one line, because it names the only way THEY can act
           on any of this. router-link, not an href: the app is hash-routed and
           a bare /support_ticket_create would leave the SPA and 404. -->
      <p v-if="!canEdit" class="tc-packages-intro">
        To enable a package,
        <router-link :to="{ name: 'support_ticket_create' }">create a support ticket</router-link>.
      </p>

      <table class="table tc-packages-table">
        <thead>
          <tr>
            <th class="tc-packages-name-col">Package</th>
            <th class="tc-packages-toggle-col">Enabled</th>
            <th class="tc-packages-price-col">
              Package Price
              <button
                type="button" class="tc-packages-help" title="What is this column?"
                aria-label="What is Package Price?"
                @click="$bvModal.show('tc-packages-what-package-price')"
              ><i class="fa fa-info-circle" aria-hidden="true"></i></button>
            </th>
            <th v-if="showInvoicedAmount" class="tc-packages-price-col tc-packages-price-head">
              Invoiced Amount
              <button
                type="button" class="tc-packages-help" title="What is this column?"
                aria-label="What is Invoiced Amount?"
                @click="$bvModal.show('tc-packages-what-invoiced')"
              ><i class="fa fa-info-circle" aria-hidden="true"></i></button>
            </th>
          </tr>
        </thead>
        <!-- a switch clears the model, so without this the table would render a
             full set of rows priced off an empty record (everything Off, the
             smallest fleet band) for the length of the reload -- wrong numbers
             read as real ones. One honest loading row instead. -->
        <tbody v-if="loading">
          <tr>
            <td :colspan="columnCount" class="tc-packages-loading">
              <b-spinner small></b-spinner> Loading packages&hellip;
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <!-- template v-for so a row can bring a section heading with it. Vue 2
               takes the key on the children, not the template. -->
          <template v-for="row in packageRows">
          <tr v-if="row.groupLabel" :key="row.rowKey + '-group'" class="tc-packages-group-row">
            <td :colspan="columnCount">{{ row.groupLabel }}</td>
          </tr>
          <tr
            :key="row.rowKey"
            :data-field="row.key || row.rowKey"
            :class="{ 'tc-packages-grouped': row.alwaysIncluded }"
          >
            <td class="tc-packages-name-col">
              <div class="tc-packages-label">
                {{ row.label }}
                <span v-if="row.derived === 'basic'" class="tc-packages-required">Required</span>
              </div>
              <div v-if="row.desc" class="tc-packages-desc">{{ row.desc }}</div>
              <!-- a package a sibling account runs is billed to the group, so it
                   sits in this total even though this account has it off -->
              <div v-if="rowBilledViaGroup(row).length" class="tc-packages-desc tc-packages-group-note">
                Enabled on {{ rowBilledViaGroup(row).join(', ') }} in the {{ billingGroup.key }} billing group.
              </div>
              <!-- metered SMS is the one usage-driven line on the price list, so
                   the spend that is moving the Alerts estimate is shown, not just
                   folded into it -->
              <div
                v-if="row.pricePackage === 'alerts' && rowCountsToward(row) && showSmsDetail"
                class="tc-packages-desc"
                :class="{ 'tc-packages-sms-over': smsExceedsTier }"
              >
                SMS metered at ${{ smsFloorPrice }}/mo<template v-if="smsAvgWindowDays">, from
                ${{ formatSmsCost(smsAvgMonthlyCost) }}/mo average usage over the past
                {{ smsAvgWindowDays }} days</template><template v-if="smsExceedsTier">
                &mdash; over the ${{ alertsBasePrice }}/mo {{ alertsBaseLabel }}</template>
              </div>
              <!-- the maintenance sync that is adding to the VSM line, named
                   rather than silently folded into the number -->
              <div
                v-if="row.pricePackage === 'vsm' && rowCountsToward(row) && hasVsmIntegration"
                class="tc-packages-desc tc-packages-surcharge"
              >
                {{ vsmIntegrations.join(' + ') }} maintenance sync:
                +${{ VSM_INTEGRATION_SURCHARGE }}/mo
              </div>
            </td>
            <td class="tc-packages-toggle-col">
              <b-form-checkbox
                v-if="row.key && canEdit"
                v-model="model[row.key]"
                value="True"
                unchecked-value="False"
                switch
                class="tc-packages-switch"
              />
              <span
                v-else
                class="tc-packages-state"
                :class="{ 'tc-packages-state-on': rowEnabled(row) }"
              >{{ rowEnabled(row) ? 'On' : 'Off' }}</span>
            </td>
            <td class="tc-packages-price-col">
              <!-- a 100+ fleet has no list price, so staff key the negotiated
                   figure here instead of the cell reading "Custom". Same record
                   field the Account & Billing page writes. Admins only: everyone
                   else reads whatever has been keyed. -->
              <span
                v-if="showOverrideInput(row) && canEdit"
                class="tc-packages-override-wrap"
                :title="estimateCellTooltip(row)"
              >
                <span class="tc-packages-override-affix">$</span>
                <b-form-input
                  size="sm"
                  type="text"
                  inputmode="numeric"
                  maxlength="3"
                  class="tc-packages-override-input"
                  :class="{ 'tc-packages-override-active': overridePriceForRow(row) != null }"
                  :value="overrideInputValue(row)"
                  :placeholder="overrideInputPlaceholder(row)"
                  @input="setOverride(row, $event)"
                />
                <span class="tc-packages-override-affix">/mo</span>
              </span>
              <span
                v-else
                :class="{ 'tc-packages-price-muted': !rowCountsToward(row) }"
                :title="estimateCellTooltip(row)"
              >{{ estimateCell(row) }}</span>
            </td>
            <td v-if="showInvoicedAmount" class="tc-packages-price-col">
              <span v-if="billingLoading" class="tc-packages-price-muted">&hellip;</span>
              <span v-else :title="actualCellTooltip(row)">{{ actualCell(row) }}</span>
            </td>
          </tr>
          </template>

          <!-- subscription lines that don't map to a package row above. they are
               real money on the invoice, so show them rather than let the Actual
               total outrun the rows that explain it. -->
          <tr v-for="entry in (showInvoicedAmount ? unmatchedStripeItems : [])" :key="'unmatched-' + entry.key" class="tc-packages-extra-row">
            <td class="tc-packages-name-col">
              <div class="tc-packages-label">{{ entry.label }}</div>
              <div class="tc-packages-desc">Billed on the subscription, not tied to a package above.</div>
            </td>
            <td class="tc-packages-toggle-col"></td>
            <td class="tc-packages-price-col"><span class="tc-packages-price-muted">&mdash;</span></td>
            <td class="tc-packages-price-col">{{ formatMoney(entry.price) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="tc-packages-total-row">
            <td class="tc-packages-name-col">Total</td>
            <td class="tc-packages-toggle-col"></td>
            <td class="tc-packages-price-col" :title="estimateTotalTooltip">{{ estimateTotalDisplay }}</td>
            <td v-if="showInvoicedAmount" class="tc-packages-price-col" :title="actualTotalTooltip">
              <a
                v-if="stripeSubscriptionUrl"
                :href="stripeSubscriptionUrl"
                target="_blank"
                rel="noopener"
              >{{ actualTotalDisplay }}</a>
              <span v-else>{{ actualTotalDisplay }}</span>
            </td>
          </tr>
        </tfoot>
      </table>

      <p v-if="!loading" class="tc-packages-footnotes">
        <span v-if="inBillingGroup" class="tc-packages-group-note">
          Billed as the {{ billingGroup.key }} billing group: {{ billingGroup.size }} accounts
          ({{ billingGroupSiblingNames.join(', ') }} and this one), {{ vehicleCount }} vehicles combined.
          Package prices and the total are the group's<template v-if="billing.shared_billing_group">,
          as are the invoiced amounts</template>, the same figures the
          Account &amp; Billing Stats page shows<template v-if="!isBillingGroupStorageTenant">;
          negotiated figures are keyed on {{ billingGroupStorageName }}</template>.
        </span>
        <span v-else-if="vehicleCount">Priced for {{ vehicleCount }} vehicles.</span>
        <span v-else>No vehicle count on file yet, so estimates use the smallest fleet band.</span>
        <span v-if="isCustomQuoteFleet">
          Fleets of {{ CUSTOM_QUOTE_VEHICLE_COUNT }}+ vehicles are quoted individually; until a
          negotiated figure is keyed, each package is estimated at the 50-99 price<template
          v-if="canEdit && isBillingGroupStorageTenant">
          &mdash; enter the negotiated monthly figure per package</template>.
        </span>
        <span v-if="showInvoicedAmount && billing.shared_billing_group && !inBillingGroup">
          This subscription is shared across {{ billing.billing_group_size }} accounts in the
          {{ billing.billing_group_key }} billing group, so the actual amounts cover more than this account.
        </span>
        <span v-if="showInvoicedAmount && billing.cancel_at_period_end">This subscription is set to cancel at the end of the current period.</span>
        <span v-if="showInvoicedAmount && billingError" class="tc-packages-price-muted">{{ billingError }}</span>
        <span v-if="smsSource.sms_third_party_sender">
          Alerts is priced at the vehicle tier: this account sends SMS through its own provider,
          so no metered usage runs through us.
        </span>
        <span v-else-if="showSmsDetail">
          The Alerts estimate is the higher of its list price and metered SMS usage:
          ${{ formatSmsCost(smsAvgMonthlyCost) }}/mo average usage<template v-if="smsAvgWindowDays">
          over the past {{ smsAvgWindowDays }} days</template>, which meters at ${{ smsFloorPrice }}/mo.
        </span>
        <span v-if="hasVsmIntegration">
          Vehicle Status Manager includes a ${{ VSM_INTEGRATION_SURCHARGE }}/mo maintenance-integration
          fee for {{ vsmIntegrations.join(' and ') }}<template v-if="vsmIntegrations.length > 1">
          (charged once, not per integration)</template>.
        </span>
      </p>

      <!-- one short answer per column. The full breakdown lives in the header
           explainer; these say what the number IS, in a sentence or two. -->
      <b-modal id="tc-packages-what-package-price" title="Package Price" ok-only ok-title="Close">
        <div class="tc-pi">
          <p>What this account pays per month for that package.</p>
          <p>It is a price agreed with Fleet Integrations where one has been set, otherwise the
            published list price for your vehicle count. For an account billed as part of a
            billing group, it is the group's figure at the group's combined vehicle count.</p>
          <p><strong>Included</strong> means the package carries no separate charge. Fleets of
            {{ CUSTOM_QUOTE_VEHICLE_COUNT }}+ vehicles are quoted individually; until an agreed figure
            is set, the 50-99 price is shown.</p>
          <p class="tc-pi-note">Alerts is the exception: it is priced from your text-message
            usage. See <em>{{ activeInfoLinkLabel }}</em> in the page header for the detail.</p>
        </div>
      </b-modal>

      <b-modal id="tc-packages-what-invoiced" title="Invoiced Amount" ok-only ok-title="Close">
        <div class="tc-pi">
          <p>What your current subscription actually bills for that package, taken from the live
            invoice rather than a price list.</p>
          <p>A dash means no line on the subscription maps to that package &mdash; either it is
            not billed separately, or it is not on this subscription at all.</p>
          <p>Where the two columns disagree, the Invoiced Amount is what you are charged.</p>
          <p class="tc-pi-note">A line billed to you that does not match any package above gets
            its own row, so the total always adds up.</p>
        </div>
      </b-modal>

      <b-modal
        id="tc-packages-pricing-info"
        title="How pricing works"
        size="lg"
        ok-only
        ok-title="Close"
        scrollable
      >
        <div class="tc-pi">
          <h6 class="tc-pi-h">Package price</h6>
          <p>Each package is priced for the whole fleet, not per vehicle and not per user,
            and the price steps up with your vehicle count. The Package Price column is the
            list price for your fleet size, or a figure agreed with us where one has been set;
            the Invoiced Amount column is what your subscription currently bills.</p>
          <p>We show you both because they can differ when your fleet size or our pricing has
            changed and your billing has not yet been updated to match.</p>
          <p>Fleets of {{ CUSTOM_QUOTE_VEHICLE_COUNT }}+ vehicles are quoted individually; the
            estimate assumes at least the 50-99 price until an agreed figure is set.</p>

          <h6 class="tc-pi-h">Alerts and SMS</h6>
          <p>Alerts is the one package that moves with use. Its price is the
            <strong>greater</strong> of its list price for your fleet size (or the figure agreed
            with us, where one is set) and what your text messaging actually costs, so light
            senders pay the list price and heavy senders pay for what they send.</p>

          <p class="tc-pi-sub">What a message costs</p>
          <p>Our carrier bills per <em>part</em>, not per message. A text holds
            {{ rateCard.gsm_single_chars }} characters in one part; anything longer is split
            into {{ rateCard.gsm_part_chars }}-character parts (the rest of each part carries
            the header that reassembles them), and every part is billed. A message to 40
            people that runs to 2 parts is 80 billable parts, not one message.</p>
          <ul class="tc-pi-list">
            <li><strong>{{ money(rateCard.aws_outbound_unit_usd) }}</strong> per outbound part
              &mdash; the carrier's own rate plus its pass-through fee</li>
            <li><strong>{{ money(rateCard.aws_inbound_unit_usd) }}</strong> per inbound message
              &mdash; replies you receive</li>
          </ul>

          <p class="tc-pi-sub">What we bill</p>
          <p>Carrier cost is marked up to cover delivery, receipt tracking and support:</p>
          <ul class="tc-pi-list">
            <li><strong>{{ rateCard.processing_multiplier }}&times;</strong> for automated
              alerts and notifications to cover both SMS and processing fees</li>
            <li><strong>{{ rateCard.group_message_multiplier }}&times;</strong> for Group
              Message sends &mdash; bulk messages you compose and send yourself</li>
          </ul>
          <p>That monthly figure is averaged over the last {{ smsAvgWindowDays || 90 }} days
            and rounded up to the next ${{ SMS_ROUNDING_INCREMENT }}.</p>

          <p class="tc-pi-sub">An example</p>
          <p>A month with 10,000 outbound parts of automated alerts and 500 replies:</p>
          <ul class="tc-pi-list">
            <li>10,000 &times; {{ money(rateCard.aws_outbound_unit_usd) }} =
              <strong>{{ dollars(10000 * rateCard.aws_outbound_unit_usd) }}</strong> of carrier
              cost, which at {{ rateCard.processing_multiplier }}&times; bills
              {{ dollars(10000 * rateCard.aws_outbound_unit_usd * rateCard.processing_multiplier) }}</li>
            <li>500 &times; {{ money(rateCard.aws_inbound_unit_usd) }} =
              <strong>{{ dollars(500 * rateCard.aws_inbound_unit_usd) }}</strong>, which bills
              {{ dollars(500 * rateCard.aws_inbound_unit_usd * rateCard.processing_multiplier) }}</li>
            <li>Together {{ dollars(exampleTotal) }}, rounded up to
              <strong>${{ exampleRounded }}/mo</strong></li>
          </ul>
          <p>If this account's Alerts list price were ${{ exampleTierBelow }}/mo, the Alerts
            row would read <strong>${{ exampleRounded }}/mo</strong> &mdash; the greater of the
            two. Had those 10,000 parts been Group Message sends instead, the same usage would
            bill {{ dollars(10000 * rateCard.aws_outbound_unit_usd * rateCard.group_message_multiplier) }}
            rather than {{ dollars(10000 * rateCard.aws_outbound_unit_usd * rateCard.processing_multiplier) }}.</p>

          <p class="tc-pi-note">Sending through your own SMS provider means no usage runs
            through us, so Alerts stays at its list price.</p>
        </div>
      </b-modal>

      <div v-if="canEdit" class="tc-packages-actions">
        <b-btn size="lg" variant="success" @click="save_config" :disabled="saving || !isDirty">
          <b-spinner v-if="saving" small></b-spinner>
          <template v-else>Save</template>
        </b-btn>
      </div>
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex'
import { get_tenant_config_settings, get_tenant_package_billing, update_tenant_config_settings } from '../networking/apis'
import dirtyTrackingMixin from '../mixins/dirty_tracking_mixin'
import { computeEffectiveAdmin } from '@/utils/view_as'
import {
  CUSTOM_QUOTE_VEHICLE_COUNT,
  PRICING_OVERRIDE_FIELD,
  SMS_ROUNDING_INCREMENT,
  VSM_INTEGRATION_SURCHARGE,
  alertsBasePrice,
  alertsMeteredPrice,
  alertsPriceFromSmsAndTier,
  buildPricingOverrideValue,
  normalizePackageLabel,
  normalizePricingOverridePackages,
  packagePriceForVehicleCount,
  parseOverrideAmount,
  roundUpTo25,
  tenantHasBasicPackage,
  vsmIntegrationLabels,
  vsmPriceWithIntegrations,
} from '../helpers/package_pricing'

// the product packages. `key` is the tenant feature flag; the nav registry
// (PACKAGE_GATES) and each Settings tab gate on the same flags, so toggling one here is what shows or
// hides that package everywhere. Stored as the 'True'/'False' strings the b-form-select emits, which
// isFeatureEnabled() already understands alongside real booleans.
//
// `pricePackage` is the key into the published price list (helpers/package_pricing.js). Two rows can
// name the same package -- Driver Manager is sold as one package and shows up here as both its
// scorecard and coaching halves -- so the price is charged to the FIRST enabled row carrying it and
// every later one reads "Included" (see estimateCell). Rows with no pricePackage are not separately
// priced on the public list.
//
// Smart Dispatching, the two VSM sub-toggles and Fleet Assistant used to have rows here. They are not
// separately sold packages (Fleet Assistant ships inside Basic Integration, the VSM toggles are
// options within VSM), so they were dropped from this page; their flags are untouched and still
// gate their own surfaces.
const PACKAGE_ROWS = [
  // not a toggle: Basic Integration is what connects the tenant's systems and is required by
  // everything else, so it carries no flag of its own. It IS billed, and appears on the Stripe
  // subscription, so it needs a row or neither total reconciles with the invoice. Whether it is
  // *active* is derived (see basicPackageActive), matching tenant_manager's tenant_has_basic_package
  // so the two pages estimate the same number.
  { rowKey: 'basic', key: null, pricePackage: 'basic', derived: 'basic',
    label: 'Basic Integration',
    desc: 'Connects your operations and telematics platforms. Required by every other package.' },
  // always included, and entirely on its own: no pricePackage at all, so it
  // never carries a charge, never takes an override, and never stands in for
  // Driver Manager in the total the way it used to when it shared that key.
  { rowKey: 'coaching_portal', key: 'coaching_portal_enabled', alwaysIncluded: true, groupLabel: 'Included with Basic Integration', label: 'Coaching Portal',
    desc: 'Coaching workflow, typically used with Driver Manager and Event Response Manager' },
  // always included: no charge of its own, so nothing to price or override
  { rowKey: 'bus_sos', key: 'bus_sos_enabled', alwaysIncluded: true, label: 'Bus SOS',
    desc: 'Emergency SOS from a driver\'s phone, routed to the right people.' },
  { rowKey: 'actuals', key: 'actuals_enabled', pricePackage: 'actuals', groupLabel: 'Priced separately', label: 'Bookings & Actuals',
    desc: 'Pulls real driver hours and vehicle mileage from telematics back into your booking platform.' },
  { rowKey: 'alerts', key: 'alerts_enabled', pricePackage: 'alerts', label: 'Alerts',
    desc: 'Custom alerts and trip notifications for drivers, dispatch, and customers.' },
  { rowKey: 'views_reports', key: 'views_reports_enabled', pricePackage: 'views_reports', label: 'Views & Reports',
    desc: 'Extra web, email, and CSV views of booking and actuals data for payroll and billing.' },
  { rowKey: 'vsm', key: 'vehicle_status_management_enabled', pricePackage: 'vsm', label: 'Vehicle Status Manager (VSM)',
    desc: 'Real-time vehicle readiness across dispatch, cleaning, and maintenance.' },
  { rowKey: 'driver_scorecard', key: 'driver_scorecard_enabled', pricePackage: 'driver_manager', label: 'Driver Manager (Scorecards)',
    desc: 'At-a-glance driver performance across safety, efficiency, and productivity.' },
  { rowKey: 'ops_desk', key: 'ops_desk_enabled', pricePackage: 'erm', label: 'Event Response Manager (ERM)',
    desc: 'One front door for breakdowns, accidents, delays, complaints, and driver kudos.' },
  // Maps is the odd one out twice over. It is NOT in the nav registry's
  // PACKAGE_GATES, so unlike every other row here, turning it off does not hide
  // anything from the rail -- it is an entitlement/billing flag, and the Map
  // Settings tab writes the same one. And map views ship inside Basic
  // Integration on the current price list, so it carries no separate charge
  // (PACKAGE_PRICING.maps is defaultPrice 0). The row still earns its place:
  // some accounts carry a legacy "Map Features Package" Stripe line, which
  // without this row shows up as an unexplained extra on the invoice side.
  { rowKey: 'maps', key: 'maps_enabled', pricePackage: 'maps', label: 'Maps',
    desc: 'Custom maps hosted on your public websites.' },
  { rowKey: 'data_services', key: 'data_services_enabled', pricePackage: 'data', label: 'Data Services',
    desc: 'General external system data integration features.' },
]

function normBool(v) {
  return (v === true || v === 'True' || v === 'true' || v === 1 || v === '1') ? 'True' : 'False'
}

// the package keys one billing-group member is billed for, from the flag
// booleans the billing endpoint returns for it. Same rules as
// tenant_manager.vue's tenant_enabled_packages (Basic is derived; Driver
// Manager follows the scorecard half only; coaching counts toward Basic), so
// the group prices here as it does on the Account & Billing Stats page.
// The Alerts package follows its own switch, and nothing else.
//
// This used to mirror the API's _rollup_alerts_enabled, which turns alerts_enabled
// on whenever ANY alert-type or trip-notification flag is set. Measured against
// tenants-prod on 2026-09-26, that roll-up changes nothing for the 70 accounts
// whose own switch is already on, and reaches exactly 3 whose switch is off:
// Venture Bus Tours, AAAHI-HSI and DEMO-CM. Venture is invoiced $300/mo -- Basic
// and Actuals, no Alerts line -- while the roll-up estimated $450. It charged for
// a package the account does not buy, on the strength of a sub-feature it does.
function alertsPackageOn(flags) {
  return normBool((flags || {}).alerts_enabled) === 'True'
}

// `tenant` carries tenant_type and telematics_type: the member projection the
// billing endpoint returns for a sibling, or this tenant's own model
function memberPackages(flags, tenant) {
  const on = key => normBool((flags || {})[key]) === 'True'
  const packages = []
  if (tenantHasBasicPackage(tenant))
    packages.push('basic')
  if (on('actuals_enabled')) packages.push('actuals')
  if (on('views_reports_enabled')) packages.push('views_reports')
  if (alertsPackageOn(flags)) packages.push('alerts')
  if (on('vehicle_status_management_enabled')) packages.push('vsm')
  if (on('ops_desk_enabled')) packages.push('erm')
  if (on('driver_scorecard_enabled')) packages.push('driver_manager')
  if (on('data_services_enabled')) packages.push('data')
  if (on('maps_enabled')) packages.push('maps')
  return packages
}

export default {
  name: 'TenantConfigPackages',
  mixins: [dirtyTrackingMixin],
  data() {
    return {
      model: {},
      loading: true,
      packageRows: PACKAGE_ROWS,
      billing: {},
      billingTenant: null,
      billingLoading: false,
      billingError: '',
      CUSTOM_QUOTE_VEHICLE_COUNT,
      SMS_ROUNDING_INCREMENT,
      VSM_INTEGRATION_SURCHARGE,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    // staff only. customer-admins reach this tab read-only: the toggles render as
    // On/Off text, the Save button is gone, and save_config refuses anyway.
    canEdit() {
      return computeEffectiveAdmin(this.user, this.viewAs)
    },
    // Package Price is for everyone who can reach the page -- it is what the
    // account pays. The Invoiced Amount is staff-only for now: it names what the
    // live subscription bills, line by line, which is a billing view rather than
    // a product one.
    showInvoicedAmount() {
      return this.canEdit
    },
    columnCount() {
      return this.showInvoicedAmount ? 4 : 3
    },
    packageKeys() {
      return PACKAGE_ROWS.filter(r => r.key).map(r => r.key)
    },
    // Basic Integration has no flag of its own: it is the booking <-> telematics
    // link, so it is on once both platforms are integrated and never otherwise.
    // Kept in step with tenant_manager.vue's tenant_has_basic_package (both call
    // helpers/package_pricing.js tenantHasBasicPackage) so the admin billing
    // page and this page estimate the same total for the same tenant.
    basicPackageActive() {
      return tenantHasBasicPackage(this.model)
    },
    // A tenant that bills as part of a billing group (the AAAHI-AAA-* sites,
    // Jefferson Lines + Karst Stage, ...) is priced by the Account & Billing
    // Stats page as ONE unit: the group's combined vehicle count, the packages
    // enabled on any member, the overrides keyed on the group's storage tenant,
    // and the members' SMS spend summed. This page shows those group figures
    // too, marked as such, so a customer-admin at one member and staff on the
    // admin page read the same numbers. The endpoint sends the block only when
    // the group has more than one active member.
    billingGroup() {
      const group = this.billing.billing_group
      return group && Number(group.size) > 1 ? group : null
    },
    inBillingGroup() {
      return !!this.billingGroup
    },
    billingGroupMembers() {
      return this.billingGroup ? (this.billingGroup.members || []) : []
    },
    billingGroupSiblingNames() {
      return this.billingGroupMembers.filter(m => !m.is_current).map(m => m.tenant_name)
    },
    // overrides live on ONE member's record (the one carrying the Stripe
    // match). Only that member's page may edit them; the others read them.
    isBillingGroupStorageTenant() {
      if (!this.billingGroup)
        return true
      const current = this.billingGroupMembers.find(m => m.is_current)
      return !!(current && current.is_storage)
    },
    billingGroupStorageName() {
      return this.billingGroup ? (this.billingGroup.storage_tenant_name || '') : ''
    },
    // every package any member is billed for: the group is one price list
    // entry. The siblings come from the endpoint's snapshot; THIS tenant's
    // packages come from the live model, so a toggle flipped on the page moves
    // the total at once, the way it does for a solo tenant, instead of after
    // the save and reload.
    billingGroupPackages() {
      const keys = new Set()
      for (const member of this.billingGroupMembers) {
        if (member.is_current)
          continue
        for (const key of memberPackages(member.flags, member))
          keys.add(key)
      }
      for (const key of memberPackages(this.model, this.model))
        keys.add(key)
      return keys
    },
    vehicleCount() {
      const raw = this.billingGroup ? this.billingGroup.vehicle_count : this.model.vehicle_count
      const count = Number(raw)
      return Number.isFinite(count) && count >= 0 ? count : 0
    },
    // the SMS figures Alerts is priced from: the group's summed spend for a
    // billing group (third-party senders excluded, as on the admin page),
    // otherwise this tenant's own, under the field names the endpoint uses
    smsSource() {
      const group = this.billingGroup
      if (!group)
        return this.billing
      const sms = group.sms || {}
      return {
        sms_sent_cost_30d: sms.sent_cost_30d,
        sms_received_cost_30d: sms.received_cost_30d,
        sms_total_cost_30d: sms.total_cost_30d,
        sms_avg_monthly_cost_90d: sms.avg_monthly_cost_90d,
        sms_avg_window_days: sms.avg_window_days,
        sms_third_party_sender: !!sms.third_party_only,
      }
    },
    // only worth showing when there is metered spend to show
    showSmsDetail() {
      return this.smsActualsAvailable && this.smsTotalCost > 0
    },
    isCustomQuoteFleet() {
      return this.vehicleCount >= CUSTOM_QUOTE_VEHICLE_COUNT
    },
    smsTotalCost() {
      const amount = Number(this.smsSource.sms_total_cost_30d)
      return Number.isFinite(amount) ? amount : 0
    },
    // what Alerts is actually priced from: average monthly spend over the
    // pricing window. The 30-day total above is reported, not charged.
    smsAvgMonthlyCost() {
      const amount = Number(this.smsSource.sms_avg_monthly_cost_90d)
      return Number.isFinite(amount) ? amount : 0
    },
    smsAvgWindowDays() {
      return Number(this.smsSource.sms_avg_window_days) || 0
    },
    // a third-party sender's messages never run through us, so there is no
    // metered spend to price Alerts from -- the vehicle tier stands alone
    smsActualsAvailable() {
      const sms = this.smsSource
      if (sms.sms_third_party_sender)
        return false
      return sms.sms_avg_monthly_cost_90d != null
        || sms.sms_total_cost_30d != null
        || sms.sms_sent_cost_30d != null
        || sms.sms_received_cost_30d != null
    },
    // RTA / Fleetio maintenance sync adds a flat $50 to VSM -- once, even with
    // both on. Read off the same tenant flags the VSM settings tab writes.
    // staff-keyed negotiated prices, { <packageKey>: "<amount>" }. Same record
    // field the Account & Billing page writes, so a figure keyed on either page
    // shows on both.
    pricingOverrides() {
      // a billing group's figures are keyed on its storage tenant. That member
      // reads and edits its own record here; every other member reads the
      // storage tenant's figures as the endpoint relayed them.
      if (this.billingGroup && !this.isBillingGroupStorageTenant)
        return normalizePricingOverridePackages({ packages: this.billingGroup.pricing_overrides || {} })
      return normalizePricingOverridePackages(this.model[PRICING_OVERRIDE_FIELD])
    },
    vsmIntegrations() {
      // one surcharge for the group: any member syncing RTA or Fleetio makes
      // it billable, and it is not charged per member
      if (this.billingGroup) {
        const labels = this.billingGroup.vsm_integrations || []
        return vsmIntegrationLabels(() => true).filter(label => labels.includes(label))
      }
      return vsmIntegrationLabels(key => normBool(this.model[key]) === 'True')
    },
    hasVsmIntegration() {
      return this.vsmIntegrations.length > 0
    },
    vsmTierPrice() {
      return packagePriceForVehicleCount('vsm', this.vehicleCount)
    },
    // Alerts is formula-priced: the greater of its base price and the metered
    // SMS figure. The base is the vehicle-tier list price, or the figure staff
    // keyed where one is set -- a keyed figure stands in for the tier (a 100+
    // fleet's negotiated price) and is a FLOOR, not a cap: metered spend above
    // it is still billed. Usage moves every month, so this is what the Account
    // & Billing Stats page resolves too (apply_monthly_pricing_overrides).
    alertsOverridePrice() {
      return this.overridePriceForPackage('alerts')
    },
    alertsBasePrice() {
      return alertsBasePrice(this.alertsTierPrice, this.alertsOverridePrice)
    },
    alertsBaseLabel() {
      return this.alertsOverridePrice != null ? 'keyed price' : 'list price'
    },
    alertsFormulaPrice() {
      return alertsPriceFromSmsAndTier(this.alertsBasePrice, this.smsAvgMonthlyCost, this.smsActualsAvailable)
    },
    // served by the billing endpoint from util/sms_rates.py, so the explainer
    // cannot drift from the rates actually charged. Falls back to the published
    // figures only so the modal still reads before billing has loaded.
    rateCard() {
      return Object.assign({
        aws_outbound_unit_usd: 0.01002,
        aws_inbound_unit_usd: 0.0075,
        processing_multiplier: 2,
        group_message_multiplier: 1.5,
        gsm_single_chars: 160,
        gsm_part_chars: 153,
      }, this.billing.rates || {})
    },
    exampleTotal() {
      const r = this.rateCard
      return (10000 * r.aws_outbound_unit_usd + 500 * r.aws_inbound_unit_usd)
        * r.processing_multiplier
    },
    exampleRounded() {
      return roundUpTo25(this.exampleTotal)
    },
    // a tier the example clearly clears, so "the greater of the two" has a point
    exampleTierBelow() {
      return Math.max(packagePriceForVehicleCount('alerts', 0), 0)
    },
    alertsTierPrice() {
      return packagePriceForVehicleCount('alerts', this.vehicleCount)
    },
    // the SMS floor: average monthly spend, rounded UP to the next $25, the
    // granularity the Stripe subscriptions are keyed at
    smsFloorPrice() {
      return this.smsActualsAvailable ? alertsMeteredPrice(this.smsAvgMonthlyCost) : 0
    },
    // an overage worth surfacing: metered spend has run past the list price the
    // account was quoted, so the Alerts estimate is now driven by usage
    // strictly "metered spend has run past a published list price". A 100+ fleet
    // has no list price to exceed, and saying it did rendered the empty
    // "over the $/mo list price" that made this line nonsense on Windstar.
    smsExceedsTier() {
      if (!this.smsActualsAvailable || this.smsFloorPrice <= 0)
        return false
      if (this.alertsBasePrice == null)
        return false
      return this.smsFloorPrice > this.alertsBasePrice
    },
    // which package each price is charged to, so Driver Manager's two rows bill once
    pricedRowKeyByPackage() {
      const owner = {}
      for (const row of PACKAGE_ROWS) {
        if (!row.pricePackage || !this.rowEnabled(row))
          continue
        if (!(row.pricePackage in owner))
          owner[row.pricePackage] = row.rowKey
      }
      return owner
    },
    // Stripe subscription lines folded onto our package keys. A subscription can
    // carry two lines for one package, so sum rather than overwrite.
    stripeAmountByPackage() {
      const amounts = {}
      for (const entry of this.billing.items || []) {
        const key = normalizePackageLabel(entry.label || entry.product_name)
        const amount = Number(entry.monthly_amount)
        if (!Number.isFinite(amount))
          continue
        amounts[key] = (amounts[key] || 0) + amount
      }
      return amounts
    },
    knownPricePackages() {
      return new Set(PACKAGE_ROWS.map(row => row.pricePackage).filter(Boolean))
    },
    unmatchedStripeItems() {
      const known = this.knownPricePackages
      return (this.billing.items || [])
        .map((entry, index) => ({
          key: index,
          label: entry.product_name || entry.label || 'Subscription',
          price: Number(entry.monthly_amount),
          packageKey: normalizePackageLabel(entry.label || entry.product_name),
        }))
        .filter(entry => Number.isFinite(entry.price) && !known.has(entry.packageKey))
    },
    // the packages in the total, in price-list order. A package that is
    // switched off is not billed, whatever is keyed against it -- the box stays
    // available so staff can set it up ahead of turning it on, and the cell
    // tooltip says it is out of the total. Each package counts once however
    // many rows carry it (Driver Manager's two halves), which is how a tenant
    // running only the Coaching half still has Driver Manager counted. In a
    // billing group the set is every package enabled on ANY member: the group
    // is billed as one fleet, so a package a sibling runs is in this total too.
    billedPackageKeys() {
      const order = []
      for (const row of PACKAGE_ROWS) {
        if (row.pricePackage && !order.includes(row.pricePackage))
          order.push(row.pricePackage)
      }
      if (this.billingGroup)
        return order.filter(key => this.billingGroupPackages.has(key))
      return order.filter(key => key in this.pricedRowKeyByPackage)
    },
    estimateTotal() {
      let total = 0
      let anyCustom = false
      for (const packageKey of this.billedPackageKeys) {
        const price = this.priceForPackage(packageKey)
        if (price == null)
          anyCustom = true
        else
          total += price
      }
      return { total, anyCustom }
    },
    estimateTotalDisplay() {
      if (this.loading)
        return '—'
      const { total, anyCustom } = this.estimateTotal
      if (anyCustom)
        return total > 0 ? '$' + total + '/mo + custom' : 'Custom quote'
      return '$' + total + '/mo'
    },
    estimateTotalTooltip() {
      const lines = [(this.billingGroup
        ? 'What the enabled packages cost the ' + this.billingGroup.key + ' billing group at ' + this.vehicleCount
          + ' vehicles combined'
        : 'What the enabled packages cost this account at ' + this.vehicleCount + ' vehicles')
        + ': list prices, or a figure agreed with us where one has been set.']
      if (this.smsActualsAvailable && this.smsTotalCost > 0)
        lines.push('Alerts reflects $' + this.formatSmsCost(this.smsAvgMonthlyCost)
          + '/mo of average metered SMS.')
      if (this.hasVsmIntegration)
        lines.push('Includes the $' + VSM_INTEGRATION_SURCHARGE + '/mo RTA / Fleetio maintenance-integration fee on VSM.')
      return lines.join('\n')
    },
    // the subscription's own monthly amount, not the sum of the rows: that is what
    // actually gets invoiced, and it stays right even when a line fails to map.
    actualTotalDisplay() {
      if (this.loading)
        return '—'
      if (this.billingLoading)
        return '…'
      const amount = Number(this.billing.monthly_amount)
      if (!this.billing.has_subscription || !Number.isFinite(amount))
        return '—'
      return '$' + amount + '/mo'
    },
    actualTotalTooltip() {
      if (!this.billing.has_subscription)
        return 'No active Stripe subscription is matched to this account.'
      const lines = ['Current Stripe subscription total.']
      if (this.billing.status)
        lines.push('Status: ' + this.billing.status)
      if (this.billing.stripe_customer_name)
        lines.push('Customer: ' + this.billing.stripe_customer_name)
      if (this.billing.enriched_at)
        lines.push('As of ' + this.billing.enriched_at)
      return lines.join('\n')
    },
    // admin-only: the endpoint withholds the subscription id from everyone else
    stripeSubscriptionUrl() {
      const id = this.billing.stripe_subscription_id
      if (!id)
        return null
      return 'https://dashboard.stripe.com/subscriptions/' + encodeURIComponent(id)
    },
    alertsEstimateTooltip() {
      const lines = ['List price at ' + this.vehicleCount + ' vehicles: '
        + (this.alertsTierPrice == null ? 'Custom quote' : '$' + this.alertsTierPrice + '/mo')]
      if (this.smsSource.sms_third_party_sender) {
        lines.push('Third-party SMS sender: priced at the vehicle tier, not metered SMS.')
        return lines.join('\n')
      }
      if (!this.smsActualsAvailable) {
        lines.push('No SMS usage figures available.')
        return lines.join('\n')
      }
      lines.push('SMS past 30 days: $' + this.formatSmsCost(this.smsTotalCost)
        + ' (sent $' + this.formatSmsCost(this.smsSource.sms_sent_cost_30d)
        + ', received $' + this.formatSmsCost(this.smsSource.sms_received_cost_30d) + ')')
      lines.push('Average monthly SMS: $' + this.formatSmsCost(this.smsAvgMonthlyCost)
        + (this.smsAvgWindowDays ? ' (over ' + this.smsAvgWindowDays + ' days)' : ''))
      lines.push('Metered at $' + this.smsFloorPrice + '/mo')
      lines.push(this.alertsBasePrice == null
        ? 'No list price at this fleet size, so Alerts is billed at the metered figure.'
        : 'Alerts is billed at the higher of the ' + this.alertsBaseLabel + ' and that figure.')
      if (this.alertsOverridePrice != null)
        lines.push('Keyed figure $' + this.alertsOverridePrice + '/mo is the floor; metered SMS above it is billed instead.')
      return lines.join('\n')
    },
    vsmEstimateTooltip() {
      const tier = this.vsmTierPrice == null
        ? 'Custom quote' : '$' + this.vsmTierPrice + '/mo'
      const lines = ['List price at ' + this.vehicleCount + ' vehicles: ' + tier]
      if (!this.hasVsmIntegration) {
        lines.push('No RTA or Fleetio maintenance sync on this account.')
        return lines.join('\n')
      }
      lines.push(this.vsmIntegrations.join(' + ') + ' maintenance sync: +$'
        + VSM_INTEGRATION_SURCHARGE + '/mo')
      if (this.vsmTierPrice == null)
        lines.push('Included in the custom quote for this fleet size.')
      else if (this.vsmIntegrations.length > 1)
        lines.push('Charged once, not per integration.')
      return lines.join('\n')
    },
    // the header control the short explainers point at
    activeInfoLinkLabel() {
      return 'How pricing works'
    },
    estimateTooltip() {
      return 'The package price for this ' + (this.billingGroup ? 'billing group' : 'account')
        + ': a figure keyed by staff, otherwise the list price from fleet-integrations.com/pricing '
        + 'for ' + (this.billingGroup ? 'the group\'s combined' : 'this') + ' vehicle count. '
        + 'The same figure the Account & Billing Stats page shows.'
    },
    actualTooltip() {
      return 'What the current subscription invoices for this account.'
    },
  },
  async mounted() {
    this.load_data()
  },
  methods: {
    rowEnabled(row) {
      if (row.derived === 'basic')
        return this.basicPackageActive
      // Alerts is billed whenever any alert type or trip notification is on,
      // not only when its own switch is -- the same roll-up the admin page uses
      if (row.pricePackage === 'alerts')
        return alertsPackageOn(this.model)
      return normBool(this.model[row.key]) === 'True'
    },
    formatMoney(amount) {
      if (amount == null || !Number.isFinite(Number(amount)))
        return '—'
      return '$' + Number(amount) + '/mo'
    },
    // Alerts is priced from metered SMS wherever we have it. Real usage beats a
    // keyed guess, and the figure moves every month, so a number typed once
    // would go stale silently -- the row locks rather than inviting one.
    rowIsIncluded(row) {
      return !!row.alwaysIncluded || !row.pricePackage || this.isCoveredByAnotherRow(row)
    },
    rowIsOverridable(row) {
      // Bus SOS and Coaching Portal are always included -- no charge of their
      // own, so nothing to override. Alerts takes a figure like any other row;
      // there it is the floor the metered SMS is measured against.
      return !(row.alwaysIncluded || !row.pricePackage)
    },
    // the one price a row shows and contributes, most specific first
    rowPrice(row) {
      if (!row.pricePackage)
        return null
      return this.priceForPackage(row.pricePackage)
    },
    priceForPackage(packageKey) {
      return this.effectivePriceForPackage(packageKey)
    },
    // a package this tenant has off but a sibling in its billing group has on:
    // billed to the group, so in this page's total. Returns the siblings' names
    // (empty when not the case) so the row can say where it is enabled.
    rowBilledViaGroup(row) {
      if (!this.billingGroup || !row.pricePackage || this.rowEnabled(row))
        return []
      return this.billingGroupMembers
        .filter(m => !m.is_current && memberPackages(m.flags, m).includes(row.pricePackage))
        .map(m => m.tenant_name)
    },
    // whether the row's package is in the total: on here, or on a sibling
    rowCountsToward(row) {
      return this.rowEnabled(row) || this.rowBilledViaGroup(row).length > 0
    },
    // the single place a package's estimate comes from, so the cell and the
    // total can never disagree. Alerts is the one package whose published price
    // is a floor rather than a cap -- metered SMS can push it above the tier.
    estimatedPriceForPackage(packageKey) {
      if (packageKey === 'vsm')
        return vsmPriceWithIntegrations(this.vsmTierPrice, this.hasVsmIntegration)
      if (packageKey === 'alerts')
        return alertsPriceFromSmsAndTier(this.alertsTierPrice, this.smsAvgMonthlyCost, this.smsActualsAvailable)
      return packagePriceForVehicleCount(packageKey, this.vehicleCount)
    },
    // unit rates run to fractions of a cent, so they need more places than money
    money(amount) {
      const value = Number(amount)
      return '$' + (Number.isFinite(value) ? value : 0).toFixed(5).replace(/0+$/, '').replace(/\.$/, '')
    },
    dollars(amount) {
      const value = Number(amount)
      return '$' + (Number.isFinite(value) ? value : 0).toFixed(2)
    },
    formatSmsCost(amount) {
      const value = Number(amount)
      return (Number.isFinite(value) ? value : 0).toFixed(2)
    },
    // a row whose package is already priced on an earlier row (Coaching Portal
    // under Driver Manager) is covered by that one, not billed again -- true
    // whether or not this particular half is switched on
    isCoveredByAnotherRow(row) {
      const owner = row.pricePackage && this.pricedRowKeyByPackage[row.pricePackage]
      return !!owner && owner !== row.rowKey
    },
    // "Included" means carries no separate charge, and is exactly two rows:
    // Bus SOS (no package on the price list at all) and Coaching Portal (sold
    // inside Driver Manager, so the charge sits on that row). Everything else
    // shows a number -- $0 included, which is Maps' real list price.
    estimateCell(row) {
      // a keyed figure wins on a row that accepts one, Included rows included --
      // except Alerts, where it is the floor and the metered SMS may sit above it
      if (this.rowIsOverridable(row) && row.pricePackage !== 'alerts') {
        const override = this.overridePriceForRow(row)
        if (override != null)
          return '$' + override + '/mo'
      }
      if (this.rowIsIncluded(row))
        return 'Included'
      const price = this.rowPrice(row)
      if (price == null)
        return 'Custom'
      return '$' + price + '/mo'
    },
    // the number this page actually shows and totals: a staff-keyed override
    // beats the list price wherever one is set, which is what a 100+ fleet
    // (quoted individually, so no list price exists) is priced at.
    overridePriceForPackage(packageKey) {
      return parseOverrideAmount(this.pricingOverrides[packageKey])
    },
    // Which slot a row's override lives in. Normally the package key, so a
    // figure keyed here and one keyed on the Account & Billing page are the same
    // record entry. The two exceptions have nowhere else to go: Coaching Portal
    // shares driver_manager with the scorecard row, and Bus SOS has no package
    // on the price list at all. Those slots are this page's own -- the billing
    // page prices by package and will ignore them.
    overrideKeyFor(row) {
      // every overridable row has a package key, and it is the same key the
      // Account & Billing page stores under
      return row.pricePackage
    },
    overridePriceForRow(row) {
      return parseOverrideAmount(this.pricingOverrides[this.overrideKeyFor(row)])
    },
    // The Estimated column resolves exactly as the Account & Billing Stats page
    // does (tenant_manager.vue effective_monthly_pricing): a figure staff keyed
    // wins, otherwise the published list price for this vehicle count, and a
    // 100+ fleet with nothing keyed reads Custom. What the subscription
    // actually invoices never stands in here -- it has its own column, and the
    // gap between the two is the point of showing both. It used to fall back to
    // the invoiced amount, which hid an under-priced line (a 69-vehicle fleet
    // billed at the 25-49 rate read $150 "estimated" beside $150 invoiced) and
    // disagreed with the admin page's estimate for the same tenant.
    effectivePriceForPackage(packageKey) {
      // Alerts: the keyed figure is a floor under the metered SMS, never the
      // final word (see alertsFormulaPrice)
      if (packageKey === 'alerts')
        return this.alertsFormulaPrice
      const override = this.overridePriceForPackage(packageKey)
      if (override != null)
        return override
      return this.estimatedPriceForPackage(packageKey)
    },
    // where the override box replaces the text: a row with no list price to
    // show (reads "Custom"), or one that already carries an override, so a
    // figure keyed on the billing page is editable here rather than stranded.
    // Any separately-priced package can be repriced, the way the Account &
    // Billing page has always allowed -- a list price is a starting point, not a
    // fact about the account. The two exclusions are structural rather than
    // policy: an "Included" row has no charge to override, and a switched-off
    // package is not in the estimate at all, so a figure keyed against it would
    // never be used.
    // Any row can be repriced -- a list price is a starting point, not a fact
    // about the account, and "Included" is a default rather than a claim that a
    // line can never carry a charge. The one exclusion is a switched-off
    // package: it is not billed, so there is nothing to price.
    showOverrideInput(row) {
      // a non-storage member of a billing group reads the group's figures; the
      // box would write to the wrong record, so it is text there
      return this.rowIsOverridable(row) && this.rowCountsToward(row) && this.isBillingGroupStorageTenant
    },
    // what the empty box falls back to, mirroring tenant_manager's
    // monthly_pricing_package_placeholder
    overrideInputPlaceholder(row) {
      if (!row.pricePackage || this.isCoveredByAnotherRow(row))
        return ''
      // the placeholder is the figure the row falls back to when the box is
      // left empty: the list price, the same fallback the price itself uses
      const listPrice = this.estimatedPriceForPackage(row.pricePackage)
      // the $ and /mo are chrome around the box, so a word like "Custom" would
      // render as "$Custom/mo". A 100+ fleet has no list price to hint at, so
      // the box is blank and the tooltip says why.
      return listPrice == null ? '' : String(listPrice)
    },
    overrideInputValue(row) {
      return this.pricingOverrides[this.overrideKeyFor(row)] || ''
    },
    setOverride(row, value) {
      // a non-storage group member reads the storage tenant's figures; writing
      // them onto this record would fork the group's price
      if (!this.isBillingGroupStorageTenant)
        return
      const packages = { ...this.pricingOverrides }
      const slot = this.overrideKeyFor(row)
      // whole dollars, three digits: maxlength stops a fourth character reaching
      // the DOM, and this stops anything non-numeric reaching the record --
      // paste and autofill bypass maxlength
      const trimmed = String(value == null ? '' : value).replace(/[^0-9]/g, '').slice(0, 3)
      if (trimmed)
        packages[slot] = trimmed
      else
        delete packages[slot]
      // $set so a field absent from the loaded record still becomes reactive,
      // and so dirty tracking sees it and the Save button lights up
      this.$set(this.model, PRICING_OVERRIDE_FIELD, buildPricingOverrideValue(packages))
    },
    estimateCellTooltip(row) {
      if (!row.pricePackage)
        return 'No separate charge — not a package on the price list.'
      if (this.isCoveredByAnotherRow(row))
        return 'Sold inside Driver Manager — the charge sits on that row, not this one.'
      // by ROW, not by package -- coaching_portal and bus_sos store in their own
      // slots, so a package-keyed lookup never sees a figure keyed against them
      if (this.overridePriceForRow(row) != null && row.pricePackage !== 'alerts') {
        if (this.billingGroup && !this.isBillingGroupStorageTenant)
          return 'Negotiated price keyed by Fleet Integrations staff for the ' + this.billingGroup.key
            + ' billing group (on ' + this.billingGroupStorageName + '), replacing the list price.'
        return 'Negotiated price keyed by Fleet Integrations staff, replacing the list price.'
      }
      const viaGroup = this.rowBilledViaGroup(row)
      if (viaGroup.length)
        return 'Not enabled on this account, but enabled on ' + viaGroup.join(', ') + ' — billed to the '
          + this.billingGroup.key + ' billing group, so it is in the total.'
      if (!this.rowEnabled(row))
        return 'Not enabled — list price shown for reference, not included in the total.'
      // the package-specific explanations come FIRST: now that every priced row
      // carries an override box, a generic "you can override this" hint would
      // otherwise shadow the SMS and maintenance-sync breakdowns entirely, and
      // those are the two numbers on this page nobody can derive by eye.
      if (row.pricePackage === 'alerts')
        return this.alertsEstimateTooltip
      if (row.pricePackage === 'vsm')
        return this.vsmEstimateTooltip
      if (this.showOverrideInput(row)) {
        return this.estimatedPriceForPackage(row.pricePackage) == null
          ? 'No list price at ' + this.vehicleCount + ' vehicles — this fleet is quoted '
            + 'individually. Enter the negotiated monthly figure.'
          : 'Falls back to the figure in the placeholder. Enter a monthly figure to override it.'
      }
      return 'List price at ' + this.vehicleCount + ' vehicles.'
    },
    actualCell(row) {
      if (!this.billing.has_subscription || !row.pricePackage)
        return '—'
      if (this.isCoveredByAnotherRow(row))
        return 'Included'
      const amount = this.stripeAmountByPackage[row.pricePackage]
      return amount == null ? '—' : '$' + amount + '/mo'
    },
    actualCellTooltip(row) {
      if (!this.billing.has_subscription)
        return 'No active Stripe subscription is matched to this account.'
      if (!row.pricePackage)
        return 'Not billed as its own subscription line.'
      if (this.isCoveredByAnotherRow(row))
        return 'Covered by the package billed above.'
      const amount = this.stripeAmountByPackage[row.pricePackage]
      if (amount == null)
        return 'No matching line on the current subscription.'
      return 'Billed on the current Stripe subscription.'
    },
    // Everything on this page is scoped to one tenant, so a switch has to drop
    // the previous tenant's numbers BEFORE the new ones land. The billing call
    // resolves the Stripe match across the whole tenant pool and is slow; without
    // this, the table went on showing the old account's toggles, prices and
    // invoice amounts for the length of that round trip, reading as though they
    // belonged to the tenant just switched to.
    //
    // billingTenant has to be cleared with it: it is the "already fetched this
    // tenant" guard, so wiping `billing` while leaving it set would strand the
    // page empty with no refetch on a switch back.
    reset_for_tenant() {
      this.model = {}
      this.billing = {}
      this.billingTenant = null
      this.billingError = ''
      this.billingLoading = false
      // nothing loaded means nothing to be dirty against; keeps Save disabled
      // and stops a stale snapshot making the new tenant look edited
      this.modelSnapshot = null
    },
    async load_data(silent = false) {
      if (!this.user || !this.user.tenant) return
      if (!silent) this.loading = true
      try {
        const { value: { data = {} } } = await get_tenant_config_settings(this.user.tenant)
        const results = data.results || {}
        // ensure every package flag exists on the model (reactive) and reads as a 'True'/'False'
        // string, whether the record stored a boolean, a string, or nothing at all
        for (const key of this.packageKeys) this.$set(results, key, normBool(results[key]))
        this.model = results
        this._resetDirtyTracking()
      } catch (e) {
        this.$emit('showAlert', this._saveErrorMessage(e), 'danger', 5000)
      }
      this.loading = false
      // Stripe is a separate, slower round trip (it resolves the match across every
      // tenant), so it loads alongside rather than blocking the toggles.
      this.load_billing()
    },
    async load_billing() {
      const tenant = this.user && this.user.tenant
      if (!tenant) return
      // the shell re-runs load_data on every switch back to this tab, and this
      // endpoint resolves the Stripe match across the whole tenant pool -- far
      // too heavy to repeat per visit. The subscription does not change because
      // someone flipped a package flag, so one fetch per tenant is enough.
      if (this.billingTenant === tenant) return
      this.billingLoading = true
      this.billingError = ''
      try {
        const { value: { data = {} } } = await get_tenant_package_billing(tenant)
        // a slow response for a tenant the admin has since switched away from
        // must not overwrite the current one
        if (this.user.tenant !== tenant) return
        // this endpoint answers with the summary at the top level, the way
        // /config/all/billing does -- NOT wrapped in `results` like
        // /config/settings (get_config returns {summary, results}). Reading
        // .results here silently yielded {} and blanked the whole column.
        this.billing = data || {}
        this.billingTenant = tenant
      } catch (e) {
        if (this.user.tenant !== tenant) return
        this.billing = {}
        // leave billingTenant unset so a later visit retries rather than
        // caching the failure for the life of the page.
        // Say WHICH failure: an empty Actual column looks identical whether the
        // route is missing, the token is stale, or the tenant simply has no
        // subscription, and chasing that difference without the status code
        // costs a round trip every time.
        const status = e && e.response && e.response.status
        this.billingError = 'Billing information is unavailable right now'
          + (status ? ' (HTTP ' + status + ')' : ' (no response)') + '.'
      } finally {
        if (this.user.tenant === tenant) this.billingLoading = false
      }
    },
    async save_config() {
      if (!this.canEdit || !this.isDirty || this.saving) return
      try {
        this.saving = true
        const dirty = this._dirtyFields()
        const payload = {}
        for (const key of this.packageKeys) if (key in dirty) payload[key] = this.model[key]
        // the negotiated-price overrides ride the same Save button as the
        // toggles; null clears the field once the last one is removed
        if (PRICING_OVERRIDE_FIELD in dirty)
          payload[PRICING_OVERRIDE_FIELD] = this.model[PRICING_OVERRIDE_FIELD] || null
        if (Object.keys(payload).length === 0) {
          this.$emit('showAlert', 'No changes to save', 'info', 3000)
          return
        }
        this.$emit('showAlert', 'Saving Changes', 'info', 5000)
        await update_tenant_config_settings(this.user.tenant, payload)
        this._resetDirtyTracking()
        await this.load_data(true)
        this.$emit('showAlert', 'Packages saved', 'success', 5000)
      } catch (e) {
        this.$emit('showAlert', this._saveErrorMessage(e), 'danger', 5000)
      } finally {
        this.saving = false
      }
    },
  },
  watch: {
    // The tab stays mounted (the shell uses v-show), so mounted() alone would keep
    // showing the tenant you just left. Watch the tenant id itself rather than the
    // whole user object: a deep watch also fired on unrelated user mutations, and
    // every one of those kicked off the billing round trip again for no reason.
    // view-as needs no reload -- it only flips canEdit, which is computed.
    'user.tenant'(next, previous) {
      if (next === previous) return
      this.reset_for_tenant()
      this.load_data()
    },
  },
}
</script>

<style scoped>
.tc-packages {
  max-width: 920px;
}
.tc-packages-intro {
  color: var(--fi-text-muted);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
  text-align: left;
}
.tc-pi {
  font-size: 14px;
  line-height: 1.55;
  color: var(--fi-text);
  text-align: left;
}
.tc-pi-h {
  font-weight: 700;
  margin-top: 18px;
  color: var(--fi-text);
}
.tc-pi-h:first-child {
  margin-top: 0;
}
.tc-pi-sub {
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--fi-text);
}
.tc-pi-list {
  padding-left: 20px;
  margin-bottom: 12px;
}
.tc-pi-note {
  color: var(--fi-text-muted);
  font-size: 13px;
  border-top: 1px solid var(--fi-line);
  padding-top: 10px;
  margin-top: 16px;
}
.tc-packages-table {
  margin-bottom: 12px;
}
/* #app sets text-align: center app-wide, and it is inherited by anything these
   rules do not name directly -- so every cell AND the blocks inside the name
   cell state their alignment explicitly. */
.tc-packages-table th,
.tc-packages-table td {
  vertical-align: middle;
}
.tc-packages-table th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fi-text-muted);
}
.tc-packages-name-col {
  text-align: left;
}
.tc-packages-toggle-col {
  width: 90px;
  text-align: left;
}
.tc-packages-price-col {
  width: 140px;
  text-align: right;
  white-space: nowrap;
}
/* "Latest Invoice Amount" does not fit on one line at this width, and forcing
   the column wider would push the package names in. Let the heading wrap; the
   amounts under it keep nowrap from the rule above. */
.tc-packages-price-head {
  white-space: normal;
}
.tc-packages-label,
.tc-packages-desc {
  text-align: left;
}
.tc-packages-label {
  font-weight: 600;
  color: var(--fi-text);
}
.tc-packages-desc {
  font-size: 12.5px;
  color: var(--fi-text-muted);
}
.tc-packages-required {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fi-text-muted);
  border: 1px solid var(--fi-line-strong);
  border-radius: 3px;
  padding: 0 4px;
  margin-left: 6px;
  vertical-align: 1px;
}
.tc-packages-state {
  font-size: 13px;
  font-weight: 600;
  color: var(--fi-text-muted);
}
.tc-packages-state-on {
  color: var(--fi-success);
}
.tc-packages-sms-over {
  color: var(--fi-warning);
  font-weight: 600;
}
.tc-packages-group-row td {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fi-text-muted);
  background: var(--fi-surface-sunken);
  padding-top: 8px;
  padding-bottom: 8px;
  text-align: left;
}
/* the indent is what says these sit under Basic, not beside it.
   !important because a global `.table td { padding: 0.1rem !important }` sets
   every cell in the app; specificity alone loses to it. */
.tc-packages-grouped .tc-packages-name-col {
  padding-left: 28px !important;
}
.tc-packages-loading {
  text-align: left;
  color: var(--fi-text-muted);
  font-size: 13px;
  padding: 18px 8px;
}
.tc-packages-override-wrap {
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 1px;
  white-space: nowrap;
}
.tc-packages-override-affix {
  color: var(--fi-text-muted);
  font-size: 13px;
}
.tc-packages-override-input {
  /* exactly three digits wide: ch is the width of a "0" in the current font, so
     this tracks the font rather than guessing a pixel count */
  width: calc(3ch + 14px);
  padding-left: 3px;
  padding-right: 3px;
  text-align: right;
  font-size: 13px;
}
.tc-packages-override-active {
  border-color: var(--fi-success);
  font-weight: 600;
}
.tc-packages-surcharge {
  color: var(--fi-text-muted);
}
.tc-packages-group-note {
  color: var(--fi-text-muted);
  font-style: italic;
}
.tc-packages-price-muted {
  color: var(--fi-text-faint);
}
.tc-packages-help {
  background: none;
  border: 0;
  padding: 0;
  color: var(--fi-text-faint);
  margin-left: 4px;
  cursor: pointer;
}
.tc-packages-help:hover {
  color: var(--fi-text-muted);
}
.tc-packages-extra-row td {
  background: var(--fi-surface-sunken);
}
.tc-packages-total-row td {
  font-weight: 700;
  color: var(--fi-text);
  border-top: 2px solid var(--fi-line-strong);
}
.tc-packages-footnotes {
  color: var(--fi-text-muted);
  font-size: 12.5px;
  line-height: 1.5;
  text-align: left;
  margin-bottom: 16px;
}
.tc-packages-footnotes span {
  display: block;
}
.tc-packages-actions {
  margin-top: 8px;
  text-align: left;
}
</style>
